import useCareAdvisor from '@brands/hooks/useCareAdvisor';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { CasePayload } from '@brands/services/cases/createNewCase';
import { Status } from '@brands/services/cases/types/ICase';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import React, { Dispatch, FormEventHandler, SetStateAction, useMemo, useRef, useState } from 'react';
import { AiFillCloseCircle } from 'react-icons/ai';

import { Loading } from '../../../../Components/LoadingSpinner/Loading';
import { IAssignCACase } from '../../../../services/cases/assignCareAdvisor';
import { CaseType, ICase } from '../../../../services/cases/types/ICase';
import { EnforceCAAssignModal } from '../../../../services/forms/types/ImageNote';
import { displayErrorDetails } from '../../../../Utils/displayError';
import styles from './styles.module.scss';

interface Modal {
  setOpenModal: React.Dispatch<React.SetStateAction<EnforceCAAssignModal>>;
  caId: number;
  caseId: number;
  setLoading: Dispatch<SetStateAction<boolean>>;
  thisCase?: ICase;
  setCase?: React.Dispatch<React.SetStateAction<ICase>>;
  assignToCareAdvisor: (case_id: number, body: IAssignCACase) => Promise<ICase | null>;
  existCAName: string;
  editCase: (case_id: number, body: CasePayload) => Promise<ICase | null>;
  /**
   * Reloads the case after a successful reassignment. The API resets a CC/OHC
   * case to accepted_care_advisor on reassign, and only the page's full case
   * refresh moves it on to in progress, so without this the new CA's controls
   * stay disabled until a manual reload.
   */
  onAssigned?: () => Promise<void>;
}

const EnforceAssignCAModal = ({
  setOpenModal,
  caId,
  caseId,
  setLoading,
  thisCase,
  setCase,
  assignToCareAdvisor,
  existCAName,
  editCase,
  onAssigned,
}: Modal): JSX.Element => {
  const roleNavigate = useRoleNavigate();
  const params = useMemo(
    () => ({
      sort_by: 'created_at' as const,
      sort_direction: 'desc' as const,
      limit: 1000,
      case_id: caseId,
    }),
    [caseId],
  );

  const { careAdvisors } = useCareAdvisor(params, true);
  const [loadingUntillAccept, setLoadingUntillAccept] = useState(false);
  const { userInfo } = useAppSelector(selectAuth);
  // Sync guard against double-fire of the submit handler. The fullscreen
  // loading-spinner replacement at line `if (loadingUntillAccept) return
  // <Loading fullScreen />` only renders after React commits the
  // setLoadingUntillAccept(true) state update, leaving a one-frame
  // window where a fast double-click on the submit button fires
  // enforceAssignCA twice. The first call reassigns successfully; the
  // second hits ERR_CASES_ASSIGN_CA_ALREADY_ASSIGNED ([10101-5]) on
  // the backend (because by then case.care_advisor_id == caId) and
  // surfaces a misleading "The care advisor is already assigned to
  // this case" toast even though the operation succeeded.
  // Same pattern as the original DO-4019 fix on submitToPatient.
  const isSubmittingRef = useRef(false);

  const enforceAssignCA = async (): Promise<void> => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      setLoadingUntillAccept(true);
      if (setLoading) setLoading(true);
      let updateCaseStatus;
      const updatedCase = await assignToCareAdvisor(caseId, { care_advisor_id: caId, enforce_assignment: true });
      if (
        userInfo.role.name === UserRoleName.CareAdvisor &&
        Number(updatedCase?.care_advisor_id) === Number(userInfo.id) &&
        (updatedCase?.type === CaseType.care_coordination || updatedCase?.type === CaseType.oral_health_coaching) &&
        updatedCase?.status === Status.AcceptedCareAdvisor &&
        // The patient hasn't scheduled yet (no video_conference_date) means this
        // case is still "upcoming" from the patient's perspective, so forcing it to
        // in-progress here would break MyCareJourney's needsIntakeCaseIds check,
        // which relies on status === Upcoming to route the patient into the
        // scheduling flow.
        updatedCase?.video_conference_date
      ) {
        const payload: CasePayload = {
          status: Status.InProgressCareAdvisor,
        };

        updateCaseStatus = await editCase(caseId, payload);
      }

      if (updatedCase && setCase && thisCase) {
        setCase({
          ...thisCase,
          status: updateCaseStatus ? updateCaseStatus.status : updatedCase.status,
          care_advisor: updatedCase.care_advisor,
          care_advisor_id: updatedCase.care_advisor_id,
          updated_at: updatedCase.updated_at,
        });
      }
      if (updatedCase && onAssigned) await onAssigned();
      roleNavigate(`/consultation-page/${caseId}`);
    } catch (error: any) {
      displayErrorDetails(error);
    } finally {
      if (setLoading) setLoading(false);
      setLoadingUntillAccept(false);
      isSubmittingRef.current = false;
    }
  };

  const onSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    await enforceAssignCA();
    setOpenModal({ isOpen: false, caId: 0 });
  };

  if (loadingUntillAccept) {
    return <Loading fullScreen />;
  }

  return (
    <div className={styles.modalBackground}>
      <div className={styles.modalContainer}>
        <div className={styles.modalCloseIcon}>
          <button type="button" onClick={() => setOpenModal({ isOpen: false, caId: 0 })} aria-label="Close Modal">
            <AiFillCloseCircle />
          </button>
        </div>
        <form onSubmit={onSubmit}>
          <div className={styles.inputsContainer}>
            <div className={styles.inputContainer}>
              <span>
                {userInfo.id === caId
                  ? `This case is already assigned to ${existCAName}, please confirm that you want to reassign this case
                      to yourself`
                  : `This case is already assigned to ${existCAName}, please confirm that you want to reassign this case
                to ${careAdvisors.find((ca) => ca.id === caId)?.full_name}`}
              </span>
            </div>
          </div>
          <div className={styles.buttonsContainer}>
            <button type="button" onClick={() => setOpenModal({ isOpen: false, caId: 0 })}>
              Cancel
            </button>
            <button type="submit">{userInfo.id === caId ? 'Reassign case to me' : 'Reassign case'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

EnforceAssignCAModal.defaultProps = {
  thisCase: undefined,
  setCase: undefined,
};
export default EnforceAssignCAModal;
